/**
 * The record over the years, day by day (6.3) — beyond the window of any one
 * rule: a calendar per year and a row per month, the newest first (owner,
 * 20 Oct 2026), a square per day — here, away,
 * the travel days, the days nobody confirmed, the trips planned ahead
 * (`inoutPresenceCalendar`). Each year says its counts in words, which is
 * what a screen reader reads, and shows them as pills beside the year; the
 * squares are a picture of them. The months before the record starts, and
 * those ahead with nothing planned, are not drawn (owner, 19 Oct 2026); a
 * month between two it reaches stays.
 */
import {
  inoutPresenceCalendar,
  inoutPresenceDate,
  inoutPresenceDrawnMonths,
  type InOutPresenceDay,
} from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import { Badge, Card, CardContent, CardHeader, CardTitle, cn } from '@tonnie/ui';

import { INOUT_CLASSES } from '../lib/inout-tone';

/** Days nobody confirmed: hatched, as on the timeline — never filled like a trip. */
const UNCERTAIN_LOOK =
  'bg-[repeating-linear-gradient(135deg,var(--color-muted-foreground)_0_1px,transparent_1px_3px)] opacity-60';

/** Each day's look — the timeline's: a trip filled in the hero's colour, a planned one dashed. */
const DAY_LOOK: Record<InOutPresenceDay, string> = {
  none: 'bg-transparent',
  here: 'bg-muted',
  away: INOUT_CLASSES.solid,
  travel: cn('ring-1 ring-inset', INOUT_CLASSES.subtle, INOUT_CLASSES.ringLine),
  uncertain: UNCERTAIN_LOOK,
  planned: cn('border border-dashed', INOUT_CLASSES.edge, INOUT_CLASSES.subtle),
};

const SHOWN: ReadonlyArray<Exclude<InOutPresenceDay, 'none'>> = [
  'here',
  'away',
  'travel',
  'uncertain',
  'planned',
];

/** A calendar month's short name, read on the calendar (UTC) — never shifted by a time zone. */
const MONTH_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', timeZone: 'UTC' };

function useDayName() {
  const { t, formatNumber } = useLocale();
  return (day: Exclude<InOutPresenceDay, 'none'>, country: string, count?: number) => {
    const n = count === undefined ? {} : { count, days: formatNumber(count) };
    switch (day) {
      case 'here':
        return count === undefined
          ? t('inout.presence.here', { country })
          : t('inout.presence.hereCount', { ...n, country });
      case 'away':
        return count === undefined ? t('inout.presence.away') : t('inout.presence.awayCount', n);
      case 'travel':
        return count === undefined
          ? t('inout.presence.travel')
          : t('inout.presence.travelCount', n);
      case 'uncertain':
        return count === undefined
          ? t('inout.presence.uncertain')
          : t('inout.presence.uncertainCount', n);
      case 'planned':
        return count === undefined
          ? t('inout.presence.planned')
          : t('inout.presence.plannedCount', n);
      default: {
        const unknown: never = day;
        throw new Error(`Unknown presence day ${String(unknown)}`);
      }
    }
  };
}

export function InOutPresenceCalendar({
  recordStartsOn,
  today,
  absences,
  plannedTrips,
  uncertainPeriods,
  countryName,
}: {
  recordStartsOn: string | null;
  today: string;
  absences: ReadonlyArray<{ departedOn: string; returnedOn: string | null }>;
  plannedTrips: ReadonlyArray<{ departsOn: string; returnsOn: string; state: string }>;
  uncertainPeriods: ReadonlyArray<{ from: string; to: string; away: boolean }>;
  countryName: string;
}) {
  const { t, formatDate } = useLocale();
  const dayName = useDayName();
  const years = inoutPresenceCalendar({
    recordStartsOn,
    today,
    absences,
    plannedTrips,
    uncertainPeriods,
  });
  if (years.length === 0) return null;
  const monthName = (year: number, month: number) =>
    formatDate(inoutPresenceDate(year, month, 1), MONTH_FORMAT);

  return (
    <Card data-testid="inout-presence-calendar">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">{t('inout.presence.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('inout.presence.subtitle')}</p>
        </div>
        <ul
          className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
          aria-hidden="true"
        >
          {SHOWN.map((day) => (
            <li key={day} className="flex items-center gap-1.5">
              <span className={cn('h-2.5 w-2.5 rounded-[2px]', DAY_LOOK[day])} />
              {dayName(day, countryName)}
            </li>
          ))}
        </ul>
      </CardHeader>
      <CardContent className="space-y-5">
        {years.map(({ year, months, counts }) => {
          const kept = SHOWN.filter((day) => counts[day] > 0);
          const summary = kept.map((day) => dayName(day, countryName, counts[day])).join(' · ');
          const drawn = inoutPresenceDrawnMonths(months);
          return (
            <section
              key={year}
              className="space-y-2"
              aria-label={t('inout.presence.year', { year, summary })}
              data-testid="inout-presence-year"
              data-year={year}
            >
              <div className="flex flex-wrap items-center gap-2" aria-hidden="true">
                <h3 className="mr-1 text-sm font-semibold tabular-nums text-foreground">{year}</h3>
                {kept.map((day) => (
                  <Badge key={day} variant="outline" className="gap-1.5 font-normal tabular-nums">
                    <span className={cn('h-2 w-2 rounded-[2px]', DAY_LOOK[day])} />
                    {dayName(day, countryName, counts[day])}
                  </Badge>
                ))}
              </div>
              <div className="overflow-x-auto" aria-hidden="true">
                <div className="inline-grid gap-[3px]">
                  {drawn.map(({ days, month }) => (
                    <div key={month} className="flex items-center gap-[3px]" data-month={month}>
                      <span className="w-9 shrink-0 text-2xs text-muted-foreground">
                        {monthName(year, month)}
                      </span>
                      {days.map((day, index) => (
                        <span
                          key={index}
                          className={cn('h-3 w-3 shrink-0 rounded-[2px]', DAY_LOOK[day])}
                          title={
                            day === 'none'
                              ? undefined
                              : `${formatDate(inoutPresenceDate(year, month, index + 1), 'calendarDay')} · ${dayName(day, countryName)}`
                          }
                          data-day={day}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
        <p className="text-xs text-muted-foreground">
          {t('inout.presence.travelNote', { country: countryName })}
        </p>
      </CardContent>
    </Card>
  );
}
